import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';

// ─── DESIGN TOKENS (NOVAA dark) ──────────────────────────────────
const T = {
  bg: 'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border: 'var(--nd-w06)',
  text: 'var(--nd-text)',
  textStrong: 'var(--nd-text-strong)',
  textMuted: 'var(--nd-tx35)',
  blue: '#3B82F6',
  blueLight: '#60A5FA',
  green: '#22D3A5',
  amber: '#F59E0B',
  red: '#F87171',
};

const API = process.env.REACT_APP_API_BASE_URL;
const IMG_BASE = API ? API.replace('/api', '') : '';

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .nv-att * { box-sizing: border-box; }
  .nv-att ::-webkit-scrollbar { width: 7px; height: 7px; }
  .nv-att ::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }
  .nv-att ::-webkit-scrollbar-track { background: transparent; }

  .nv-att table { border-collapse: separate; border-spacing: 0; width: 100%; }
  .nv-att th {
    font-size: 9px; letter-spacing: 0.12em; color: var(--nd-tx35); font-weight: 700;
    text-align: left; padding: 11px 14px; white-space: nowrap;
    position: sticky; top: 0; z-index: 2;
    background: var(--nd-surface); border-bottom: 1px solid var(--nd-w10);
  }
  .nv-att th.nv-sortable { cursor: pointer; user-select: none; }
  .nv-att th.nv-sortable:hover { color: var(--nd-text); }
  .nv-att th.nv-active { color: var(--nd-text-strong); }
  .nv-att td { font-size: 12px; color: var(--nd-text); padding: 9px 14px; border-bottom: 1px solid var(--nd-w05); }
  .nv-att tbody tr:hover td { background: rgba(96,165,250,0.05); }
  .nv-att tr.nv-drill { cursor: pointer; }
  .nv-att td.nv-num { font-family: 'DM Mono', monospace; }
  .nv-att tfoot td {
    position: sticky; bottom: 0; z-index: 2;
    background: var(--nd-surface); border-top: 1px solid var(--nd-w10); border-bottom: none;
    font-weight: 800; font-size: 11px;
  }
  .nv-att .nv-scroll { max-height: 560px; overflow: auto; }

  .nv-att input, .nv-att select {
    background: var(--nd-w05); border: 1px solid var(--nd-w10); border-radius: 8px;
    color: var(--nd-text); font-family: 'DM Sans', sans-serif; font-size: 13px;
    padding: 8px 12px; outline: none;
  }
  .nv-att input:focus, .nv-att select:focus { border-color: rgba(96,165,250,0.5); }
  .nv-att input[type="date"] { color-scheme: dark; }

  .nv-att .nv-tab {
    padding: 14px 18px; background: none; border: none; border-bottom: 2px solid transparent;
    color: var(--nd-tx35); font-family: 'DM Sans', sans-serif; font-weight: 700; font-size: 12.5px;
    cursor: pointer; transition: color .15s, border-color .15s; white-space: nowrap;
  }
  .nv-att .nv-tab:hover { color: var(--nd-text); }
  .nv-att .nv-tab.nv-on { color: var(--nd-text-strong); border-bottom-color: #3B82F6; }

  .nv-att .nv-fieldlabel { font-size: 8.5px; letter-spacing: 0.16em; color: var(--nd-tx35); font-weight: 700; margin-bottom: 6px; }
`;

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const UNASSIGNED = 'Unassigned';

const VIEWS = [
  { key: 'person',   tab: 'Person',        col: 'Field Officer' },
  { key: 'client',   tab: 'Client',        col: 'Client' },
  { key: 'city',     tab: 'City',          col: 'City' },
  { key: 'checkins', tab: 'Check-in log',  col: 'Name' },
];

const pad = (n) => String(n).padStart(2, '0');
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
// Start of an inclusive N-day window ending today. The API filters
// `>= from AND <= to`, so "last 7 days" is today plus the 6 before it —
// subtracting a full 7 would span 8 calendar days.
const windowStart = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - (days - 1));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const monthStartStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`; };

const fmtDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = String(s).split('-');
  if (!y || !m || !d) return s;
  return `${MONTHS[Number(m) - 1]} ${Number(d)}, ${y}`;
};
// "Apr 23 – Jul 21, 2026" — the year collapses when both ends share it.
const fmtRange = (a, b) => {
  if (!a || !b) return null;
  const [ay, am, ad] = String(a).split('-');
  const [by, bm, bd] = String(b).split('-');
  if (!ay || !by) return null;
  const left = `${MONTHS[Number(am) - 1]} ${Number(ad)}`;
  const right = `${MONTHS[Number(bm) - 1]} ${Number(bd)}, ${by}`;
  return ay === by ? `${left} – ${right}` : `${left}, ${ay} – ${right}`;
};
const fmtTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};
const pct = (n, total) => (!total ? 0 : Math.round((n / total) * 100));

const csvCell = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return `"${String(v).replace(/"/g, '""')}"`;
};
const saveCSV = (filename, headers, rows) => {
  const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Generic column sorter. Nulls always sort last regardless of direction, so an
// empty cell never outranks a real value.
const sortBy = (arr, key, dir, type) => {
  const sign = dir === 'asc' ? 1 : -1;
  return [...arr].sort((x, y) => {
    const a = x[key];
    const b = y[key];
    const aEmpty = a === null || a === undefined || a === '';
    const bEmpty = b === null || b === undefined || b === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    if (type === 'num') return (Number(a) - Number(b)) * sign;
    return String(a).localeCompare(String(b)) * sign;
  });
};

const AttendanceDashboard = () => {
  const [view, setView] = useState('person');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [activePreset, setActivePreset] = useState('all');
  const [search, setSearch] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientLabel, setClientLabel] = useState('');
  const [city, setCity] = useState('');
  const [attention, setAttention] = useState(''); // '' | 'missing_out' | 'unlinked'
  const [sort, setSort] = useState({ key: 'checkins', dir: 'desc' });
  const [showTrend, setShowTrend] = useState(true);

  const [rows, setRows] = useState([]);
  const [records, setRecords] = useState([]);
  const [kpis, setKpis] = useState({});
  const [daily, setDaily] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ clients: [], cities: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const isLog = view === 'checkins';
  const activeView = VIEWS.find(v => v.key === view) || VIEWS[0];

  // ─── columns drive header, sorting and CSV from one definition ───
  const columns = useMemo(() => {
    if (isLog) {
      return [
        { key: 'fo_name', label: 'Name', type: 'text' },
        { key: 'attendance_date', label: 'Date', type: 'text' },
        { key: '_seqOf', label: 'Of day', type: 'num', hint: 'How many shifts this person worked that day. Double shifts are allowed and every one is counted.' },
        { key: 'clock_in_at', label: 'In', type: 'text' },
        { key: 'clock_out_at', label: 'Out', type: 'text' },
        { key: 'hours_worked', label: 'Hours', type: 'num' },
        { key: 'client_name', label: 'Client', type: 'text' },
        { key: 'site_name', label: 'Site', type: 'text' },
        { key: 'city', label: 'City', type: 'text' },
        { key: 'shift', label: 'Shift', type: 'text' },
        { key: 'status', label: 'Status', type: 'text' },
        { key: '_photos', label: 'Photos', type: 'text', sortable: false },
      ];
    }
    return [
      { key: 'label', label: activeView.col, type: 'text' },
      { key: 'jobs', label: 'Jobs', type: 'num', bar: T.blueLight, hint: 'Distinct jobs checked into. One job worked by two people counts once for each of them.' },
      { key: 'checkins', label: 'Check-ins', type: 'num', bar: T.blue, hint: 'Every shift counts: one per person, per day, per job. A double-shift day contributes two.' },
      view === 'person'
        ? { key: 'days', label: 'Days', type: 'num', hint: 'Distinct dates worked. Lower than check-ins whenever double shifts were worked.' }
        : { key: 'people', label: 'People', type: 'num', hint: 'Distinct field officers who checked in.' },
      { key: 'total_hours', label: 'Total hrs', type: 'num', hint: 'Sums only shifts that have a clock-out. Open shifts are excluded, so treat this as a lower bound.' },
      // Explicitly per shift: a multi-job day logs several shifts, so this is
      // NOT hours per day and must not be labelled as though it were.
      { key: 'avg_hours', label: 'Avg hrs/shift', type: 'num', hint: 'Average per shift, NOT per day. Someone working 3 shifts in a day totals roughly 3x this — use Total hrs for the real figure.' },
      { key: 'missing_clockout', label: 'No clock-out', type: 'num', warn: true, hint: 'Check-ins with no clock-out recorded — these are excluded from hours.' },
    ];
  }, [isLog, view, activeView.col]);

  const buildQS = (extra = {}) => {
    const qs = new URLSearchParams();
    if (from) qs.append('from', from);
    if (to) qs.append('to', to);
    if (clientId) qs.append('client_id', clientId);
    if (city) qs.append('city', city);
    Object.entries(extra).forEach(([k, v]) => v && qs.append(k, v));
    return qs.toString() ? `?${qs.toString()}` : '';
  };

  const fetchData = async () => {
    try {
      setLoading(true); setError(null);
      const summaryGroup = isLog ? 'person' : view;
      const calls = [axios.get(`${API}/attendance/summary${buildQS({ group_by: summaryGroup })}`)];
      if (isLog) calls.push(axios.get(`${API}/attendance/records${buildQS()}`));
      const [sumRes, recRes] = await Promise.all(calls);
      setKpis(sumRes.data?.kpis || {});
      setDaily(sumRes.data?.daily || []);
      setRows(isLog ? [] : (sumRes.data?.rows || []));
      setRecords(isLog ? (recRes?.data?.records || []) : []);
    } catch (err) {
      setError('Failed to load attendance data');
      setRows([]); setRecords([]); setKpis({}); setDaily([]);
    } finally { setLoading(false); }
  };

  // Options come from their own call: once a client filter is applied /summary
  // reports only that client, which would collapse the dropdown to one option.
  const fetchFilterOptions = async () => {
    const qs = new URLSearchParams();
    if (from) qs.append('from', from);
    if (to) qs.append('to', to);
    const q = qs.toString() ? `?${qs.toString()}` : '';
    try {
      const res = await axios.get(`${API}/attendance/filters${q}`);
      setFilterOptions({ clients: res.data?.clients || [], cities: res.data?.cities || [] });
    } catch (e) { /* keep whatever options we already have */ }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [from, to, clientId, city, view]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFilterOptions(); }, [from, to]);

  // Keep the sort on a column that exists in the current view.
  useEffect(() => {
    if (!columns.some(c => c.key === sort.key)) {
      setSort(isLog ? { key: 'attendance_date', dir: 'desc' } : { key: 'checkins', dir: 'desc' });
    }
  }, [columns, sort.key, isLog]);

  const applyPreset = (key) => {
    setActivePreset(key);
    if (key === 'all') { setFrom(''); setTo(''); }
    else if (key === '7') { setFrom(windowStart(7)); setTo(todayStr()); }
    else if (key === '30') { setFrom(windowStart(30)); setTo(todayStr()); }
    else if (key === 'month') { setFrom(monthStartStr()); setTo(todayStr()); }
  };

  const clearAll = () => {
    setClientId(''); setClientLabel(''); setCity(''); setSearch(''); setAttention('');
    applyPreset('all');
  };

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setSort(s => (s.key === col.key
      ? { key: col.key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key: col.key, dir: col.type === 'num' ? 'desc' : 'asc' }));
  };

  const switchView = (key) => { setView(key); setAttention(''); };

  const drillInto = (row) => {
    if (view === 'client') {
      if (!row.group_id) return;
      setClientId(String(row.group_id)); setClientLabel(row.label); setView('person');
    } else if (view === 'city') {
      if (!row.label || row.label === UNASSIGNED) return;
      setCity(row.label); setView('person');
    }
  };
  const canDrill = (row) => (
    (view === 'client' && !!row.group_id) ||
    (view === 'city' && row.label && row.label !== UNASSIGNED)
  );

  const showAttention = (kind) => { setView('checkins'); setAttention(kind); };

  // ─── derived table data ───
  const q = search.trim().toLowerCase();

  // Double shifts are allowed and every one counts. A person working several
  // jobs in a day therefore logs several check-ins, all of them valid. Tag each
  // row with its position within that person-day so a shift reads as "2 of 3"
  // rather than looking like a duplicate.
  const enrichedRecords = useMemo(() => {
    const byDay = new Map();
    records.forEach(r => {
      const k = `${r.fo_name}||${r.attendance_date}`;
      if (!byDay.has(k)) byDay.set(k, []);
      byDay.get(k).push(r);
    });

    const meta = new Map();
    byDay.forEach(list => {
      const ordered = [...list].sort(
        (a, b) => new Date(a.clock_in_at || 0) - new Date(b.clock_in_at || 0),
      );
      ordered.forEach((r, i) => meta.set(r.id, { seq: i + 1, of: ordered.length }));
    });

    return records.map(r => {
      const m = meta.get(r.id) || { seq: 1, of: 1 };
      return { ...r, _seq: m.seq, _seqOf: m.of };
    });
  }, [records]);

  const visibleRows = useMemo(() => {
    const filtered = q ? rows.filter(r => (r.label || '').toLowerCase().includes(q)) : rows;
    return sortBy(filtered, sort.key, sort.dir, columns.find(c => c.key === sort.key)?.type || 'text');
  }, [rows, q, sort, columns]);

  const visibleRecords = useMemo(() => {
    let list = enrichedRecords;
    if (attention === 'missing_out') list = list.filter(r => !r.clock_out_at);
    else if (attention === 'unlinked') list = list.filter(r => !r.job_id);
    if (q) list = list.filter(r => (r.fo_name || '').toLowerCase().includes(q));
    return sortBy(list, sort.key, sort.dir, columns.find(c => c.key === sort.key)?.type || 'text');
  }, [enrichedRecords, attention, q, sort, columns]);

  const shownCount = isLog ? visibleRecords.length : visibleRows.length;

  // Bar scale is per-column across the currently visible rows.
  const maxima = useMemo(() => {
    const m = {};
    columns.filter(c => c.bar).forEach(c => {
      m[c.key] = visibleRows.reduce((acc, r) => Math.max(acc, Number(r[c.key]) || 0), 0);
    });
    return m;
  }, [columns, visibleRows]);

  const headline = useMemo(() => {
    const explicit = fmtRange(from, to);
    if (explicit) return explicit;
    if (daily.length) return fmtRange(daily[0].date, daily[daily.length - 1].date);
    return 'All time';
  }, [from, to, daily]);

  const totalCheckins = kpis.total_checkins ?? 0;
  const missingOut = kpis.missing_clockout ?? 0;
  const unlinked = kpis.unlinked_checkins ?? 0;
  const hasFilters = !!(clientId || city || attention || q);

  const rangeTag = (from && to) ? `${from}_to_${to}` : 'All_Time';
  const exportCSV = () => {
    const cols = columns.filter(c => c.key !== '_photos');
    if (isLog) {
      if (!visibleRecords.length) return;
      saveCSV(
        `Attendance_Log_${rangeTag}.csv`,
        cols.map(c => c.label),
        visibleRecords.map(r => cols.map(c => {
          if (c.key === 'attendance_date') return csvCell(fmtDate(r.attendance_date));
          if (c.key === 'clock_in_at') return csvCell(fmtTime(r.clock_in_at));
          if (c.key === 'clock_out_at') return csvCell(r.clock_out_at ? fmtTime(r.clock_out_at) : 'no out');
          if (c.type === 'num') return r[c.key] != null ? r[c.key] : '—';
          return csvCell(r[c.key]);
        })),
      );
      return;
    }
    if (!visibleRows.length) return;
    saveCSV(
      `Attendance_By_${activeView.tab}_${rangeTag}.csv`,
      [...cols.map(c => c.label), 'Last Seen'],
      visibleRows.map(r => [
        ...cols.map(c => (c.type === 'num' ? (r[c.key] != null ? r[c.key] : '—') : csvCell(r[c.key]))),
        csvCell(r.last_seen ? fmtDate(r.last_seen) : ''),
      ]),
    );
  };

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' };

  const Thumb = ({ url, tag }) => (
    url ? (
      <img
        src={`${IMG_BASE}${url}`} alt={tag}
        // Originals are ~3 MB each and render at 30px. Without lazy loading the
        // log pulls every photo in the result set on open (~1.5 GB today).
        // A real fix is server-side thumbnails; this just stops the stampede.
        loading="lazy"
        decoding="async"
        width={30} height={30}
        onClick={(e) => { e.stopPropagation(); setLightbox(`${IMG_BASE}${url}`); }}
        style={{ width: 30, height: 30, borderRadius: 5, objectFit: 'cover', cursor: 'pointer', border: '1px solid var(--nd-w12)' }}
        title={`${tag} photo — click to enlarge`}
      />
    ) : <span style={{ fontSize: 9, color: 'var(--nd-w20)' }}>{tag} —</span>
  );

  // Named to avoid colliding with recharts' <Bar>.
  const MagnitudeBar = ({ value, max, color }) => {
    const w = max > 0 ? Math.max(2, (Number(value) / max) * 100) : 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <div style={{ width: 46, height: 5, background: 'var(--nd-w06)', borderRadius: 3, overflow: 'hidden', flexShrink: 0 }}>
          <div style={{ width: `${w}%`, height: '100%', background: color, borderRadius: 3 }} />
        </div>
        <span style={{ minWidth: 26 }}>{value ?? 0}</span>
      </div>
    );
  };

  const Field = ({ label, children }) => (
    <div>
      <div className="nv-fieldlabel">{label}</div>
      {children}
    </div>
  );

  return (
    <div className="nv-att" style={{ background: T.bg, fontFamily: "'DM Sans', sans-serif", color: T.text, padding: '14px 18px', minHeight: '100vh' }}>
      <style>{GLOBAL}</style>

      {/* ─── Header: what am I looking at ─── */}
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ padding: '20px 24px 18px' }}>
          <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.18em', marginBottom: 6 }}>NOVAA · C&I MANAGEMENT</div>
          <div style={{ fontSize: 27, fontWeight: 800, color: T.textStrong, letterSpacing: '-0.03em', lineHeight: 1 }}>Attendance</div>
          <div style={{ fontSize: 12.5, color: T.textMuted, marginTop: 8 }}>
            {loading ? 'Loading…' : (
              <>
                <span style={{ color: T.text, fontWeight: 600 }}>{headline}</span>
                {' · '}
                <span style={{ color: T.text, fontWeight: 600 }}>{totalCheckins}</span> check-ins across{' '}
                <span style={{ color: T.text, fontWeight: 600 }}>{kpis.total_jobs ?? 0}</span> jobs
                {' · '}
                <span style={{ color: T.text, fontWeight: 600 }}>{kpis.people ?? 0}</span> people
              </>
            )}
          </div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />

        {/* ─── Filters ─── */}
        <div style={{ padding: '14px 24px 16px', display: 'flex', alignItems: 'flex-end', gap: 18, flexWrap: 'wrap' }}>
          <Field label="PERIOD">
            <div style={{ display: 'flex', gap: 4, background: 'var(--nd-w04)', padding: 3, borderRadius: 9, border: `1px solid ${T.border}` }}>
              {[['all', 'All time'], ['7', '7d'], ['30', '30d'], ['month', 'This month']].map(([key, label]) => (
                <button key={key} onClick={() => applyPreset(key)}
                  style={{ padding: '7px 13px', background: activePreset === key ? 'linear-gradient(135deg,#3B82F6,#22D3A5)' : 'transparent', color: activePreset === key ? T.textStrong : T.textMuted, border: 'none', borderRadius: 7, fontWeight: 700, cursor: 'pointer', fontSize: 11.5, fontFamily: "'DM Sans', sans-serif" }}>
                  {label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="CUSTOM RANGE">
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <input type="date" value={from} onChange={e => { setFrom(e.target.value); setActivePreset(''); }} />
              <span style={{ color: T.textMuted, fontSize: 12 }}>→</span>
              <input type="date" value={to} onChange={e => { setTo(e.target.value); setActivePreset(''); }} />
            </div>
          </Field>

          <Field label="CLIENT">
            <select
              value={clientId}
              onChange={(e) => {
                const id = e.target.value;
                const found = filterOptions.clients.find(c => String(c.client_id) === id);
                setClientId(id); setClientLabel(found ? found.client_name : '');
              }}
              style={{ minWidth: 180 }}
            >
              <option value="">All clients</option>
              {filterOptions.clients.map(c => <option key={c.client_id} value={c.client_id}>{c.client_name}</option>)}
            </select>
          </Field>

          <Field label="CITY">
            <select value={city} onChange={(e) => setCity(e.target.value)} style={{ minWidth: 140 }}>
              <option value="">All cities</option>
              {filterOptions.cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <Field label="SEARCH">
            <input placeholder={isLog ? 'Name…' : `${activeView.col}…`} value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 150 }} />
          </Field>

          {hasFilters && (
            <button onClick={clearAll}
              style={{ padding: '9px 14px', background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, cursor: 'pointer', fontSize: 11.5, fontWeight: 700 }}>
              Clear all
            </button>
          )}
          <button onClick={fetchData} style={{ marginLeft: 'auto', padding: '9px 14px', background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 8, color: T.textMuted, cursor: 'pointer', fontSize: 11.5, fontWeight: 700 }}>↻ Refresh</button>
        </div>
      </div>

      {error && <div style={{ ...card, padding: '14px 20px', marginBottom: 12, color: T.red, fontSize: 13 }}>⚠ {error}</div>}

      {/* ─── Summary: facts vs problems ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 12, marginBottom: 12 }}>
        <div style={{ ...card, padding: '18px 22px' }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: T.textMuted, letterSpacing: '0.16em', marginBottom: 16 }}>AT A GLANCE</div>
          <div style={{ display: 'flex', gap: 34, flexWrap: 'wrap' }}>
            {[
              ['JOBS', kpis.total_jobs ?? 0, T.blueLight],
              ['CHECK-INS', totalCheckins, T.textStrong],
              ['PEOPLE', kpis.people ?? 0, '#A78BFA'],
            ].map(([label, value, color]) => (
              <div key={label}>
                <div style={{ fontSize: 32, fontWeight: 800, color, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{loading ? '—' : value}</div>
                <div style={{ fontSize: 8.5, color: T.textMuted, letterSpacing: '0.14em', marginTop: 7, fontWeight: 700 }}>{label}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, paddingTop: 13, borderTop: `1px solid ${T.border}`, fontSize: 11.5, color: T.textMuted }}>
            <span style={{ color: T.green, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{kpis.avg_hours != null ? kpis.avg_hours : '—'}</span> avg hours per shift
            {' · '}
            <span style={{ color: T.text, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{kpis.active_days ?? 0}</span> active days
          </div>
        </div>

        <div style={{ ...card, padding: '18px 22px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: T.textMuted, letterSpacing: '0.16em', marginBottom: 16 }}>NEEDS ATTENTION</div>
          {(missingOut === 0 && unlinked === 0) ? (
            <div style={{ fontSize: 12.5, color: T.green, flex: 1, display: 'flex', alignItems: 'center' }}>✓ Nothing outstanding in this range</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
              {[
                ['missing_out', missingOut, 'missing clock-out', T.amber],
                ['unlinked', unlinked, 'no job linked', '#A78BFA'],
              ].filter(([, n]) => n > 0).map(([kind, n, label, color]) => (
                <button key={kind} onClick={() => showAttention(kind)}
                  style={{ display: 'flex', alignItems: 'baseline', gap: 11, background: attention === kind ? 'rgba(245,158,11,0.10)' : 'none', border: `1px solid ${attention === kind ? 'rgba(245,158,11,0.35)' : 'transparent'}`, borderRadius: 9, padding: '7px 9px', margin: '0 -9px', cursor: 'pointer', textAlign: 'left', width: 'calc(100% + 18px)' }}
                  title="Show these check-ins in the log">
                  <span style={{ fontSize: 25, fontWeight: 800, color, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{n}</span>
                  <span style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{label}</span>
                  <span style={{ fontSize: 11, color: T.textMuted, fontFamily: "'DM Mono', monospace", marginLeft: 'auto' }}>{pct(n, totalCheckins)}%</span>
                </button>
              ))}
              <div style={{ fontSize: 10, color: 'var(--nd-w20)', marginTop: 'auto' }}>Click a figure to list those check-ins.</div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Trend (collapsible) ─── */}
      <div style={{ ...card, marginBottom: 12 }}>
        <button onClick={() => setShowTrend(s => !s)}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '13px 22px', background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.16em' }}>DAILY TREND</span>
          <span style={{ fontSize: 10, color: 'var(--nd-w20)' }}>{showTrend ? '▴ hide' : '▾ show'}</span>
        </button>
        {showTrend && (
          <div style={{ padding: '0 18px 16px' }}>
            {daily.length === 0 ? (
              <div style={{ padding: '26px 0', textAlign: 'center', color: T.textMuted, fontSize: 12 }}>No data for this range</div>
            ) : (
              <ResponsiveContainer width="100%" height={170}>
                <BarChart data={daily} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--nd-w06)" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: 'var(--nd-tx40)', fontSize: 10 }} tickFormatter={(d) => { const p = String(d).split('-'); return p.length === 3 ? `${MONTHS[Number(p[1]) - 1]} ${Number(p[2])}` : d; }} />
                  <YAxis allowDecimals={false} tick={{ fill: 'var(--nd-tx40)', fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: 'var(--nd-surface-2)', border: '1px solid var(--nd-w10)', borderRadius: 8, color: 'var(--nd-text)', fontSize: 12 }} labelFormatter={fmtDate} cursor={{ fill: 'rgba(96,165,250,0.08)' }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: 'var(--nd-tx35)' }} />
                  <Bar dataKey="jobs" name="Jobs" fill={T.blueLight} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="checkins" name="Check-ins" fill={T.blue} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        )}
      </div>

      {/* ─── The table ─── */}
      <div style={{ ...card }}>
        <div style={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${T.border}`, paddingRight: 18, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', paddingLeft: 8 }}>
            {VIEWS.map(v => (
              <button key={v.key} className={`nv-tab${view === v.key ? ' nv-on' : ''}`} onClick={() => switchView(v.key)}>
                {v.tab}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 13 }}>
            {attention && (
              <button onClick={() => setAttention('')}
                style={{ padding: '5px 10px', background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 999, color: T.amber, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                {attention === 'missing_out' ? 'missing clock-out' : 'no job linked'} ✕
              </button>
            )}
            {clientId && (
              <button onClick={() => { setClientId(''); setClientLabel(''); }}
                style={{ padding: '5px 10px', background: 'rgba(96,165,250,0.14)', border: '1px solid rgba(96,165,250,0.35)', borderRadius: 999, color: '#93C5FD', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                {clientLabel || `Client #${clientId}`} ✕
              </button>
            )}
            {city && (
              <button onClick={() => setCity('')}
                style={{ padding: '5px 10px', background: 'rgba(34,211,165,0.14)', border: '1px solid rgba(34,211,165,0.35)', borderRadius: 999, color: '#5EEAD4', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                {city} ✕
              </button>
            )}
            <span style={{ fontSize: 10.5, color: T.textMuted, fontFamily: "'DM Mono', monospace" }}>{shownCount} rows</span>
            <button onClick={exportCSV} disabled={shownCount === 0}
              style={{ padding: '6px 12px', background: 'var(--nd-w05)', border: `1px solid ${T.border}`, borderRadius: 6, color: shownCount === 0 ? 'var(--nd-w20)' : T.text, cursor: shownCount === 0 ? 'not-allowed' : 'pointer', fontSize: 11, fontWeight: 600 }}>
              ⤓ CSV
            </button>
          </div>
        </div>

        {(view === 'client' || view === 'city') && (
          <div style={{ padding: '9px 18px', fontSize: 10.5, color: 'var(--nd-w20)', borderBottom: `1px solid ${T.border}` }}>
            Click any row to filter to it and see the people behind the numbers.
          </div>
        )}

        <div className="nv-scroll">
          <table>
            <thead>
              <tr>
                {columns.map(c => {
                  const on = sort.key === c.key;
                  return (
                    <th key={c.key}
                      className={`${c.sortable === false ? '' : 'nv-sortable'}${on ? ' nv-active' : ''}`}
                      onClick={() => toggleSort(c)}
                      title={c.hint || (c.sortable === false ? undefined : 'Sort')}>
                      {c.label}
                      {on && <span style={{ marginLeft: 5 }}>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length} style={{ textAlign: 'center', color: T.textMuted, padding: '40px' }}>Loading…</td></tr>
              ) : isLog ? (
                visibleRecords.length === 0 ? (
                  <tr><td colSpan={columns.length} style={{ textAlign: 'center', color: T.textMuted, padding: '40px' }}>No check-ins match these filters</td></tr>
                ) : visibleRecords.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 700 }}>{r.fo_name}</td>
                    <td style={{ color: T.textMuted }}>{fmtDate(r.attendance_date)}</td>
                    <td>
                      {r._seqOf > 1 ? (
                        <span
                          title={`Shift ${r._seq} of ${r._seqOf} worked that day — all of them count`}
                          style={{
                            fontSize: 9.5, fontWeight: 800, fontFamily: "'DM Mono', monospace",
                            padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap',
                            color: T.blueLight, background: 'rgba(96,165,250,0.12)',
                          }}>
                          {r._seq} of {r._seqOf}
                        </span>
                      ) : <span style={{ color: 'var(--nd-w20)', fontSize: 11 }}>—</span>}
                    </td>
                    <td className="nv-num">{fmtTime(r.clock_in_at)}</td>
                    <td className="nv-num" style={{ color: r.clock_out_at ? T.text : T.amber }}>{r.clock_out_at ? fmtTime(r.clock_out_at) : 'no out'}</td>
                    <td className="nv-num">{r.hours_worked != null ? r.hours_worked : '—'}</td>
                    <td>{r.client_name || <span style={{ color: 'var(--nd-w20)' }}>{UNASSIGNED}</span>}</td>
                    <td style={{ color: T.textMuted }}>{r.site_name || '—'}</td>
                    <td style={{ color: T.textMuted }}>{r.city || '—'}</td>
                    <td style={{ color: T.textMuted, textTransform: 'capitalize' }}>{r.shift || '—'}</td>
                    <td><span style={{ fontSize: 9, fontWeight: 800, color: T.green, background: 'rgba(34,211,165,0.12)', padding: '2px 8px', borderRadius: 5 }}>{(r.status || '').toUpperCase()}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <Thumb url={r.clock_in_image_url} tag="IN" />
                        <Thumb url={r.clock_out_image_url} tag="OUT" />
                      </div>
                    </td>
                  </tr>
                ))
              ) : visibleRows.length === 0 ? (
                <tr><td colSpan={columns.length} style={{ textAlign: 'center', color: T.textMuted, padding: '40px' }}>No data for these filters</td></tr>
              ) : visibleRows.map(r => (
                <tr key={`${r.group_id ?? 'x'}-${r.label}`}
                  className={canDrill(r) ? 'nv-drill' : undefined}
                  onClick={() => canDrill(r) && drillInto(r)}
                  title={canDrill(r) ? `Filter to ${r.label}` : undefined}>
                  <td style={{ fontWeight: 700, color: r.label === UNASSIGNED ? T.textMuted : T.text }}>
                    {r.label}
                    {r.label === UNASSIGNED && <span style={{ fontSize: 9, color: 'var(--nd-w20)', marginLeft: 7 }}>no job link</span>}
                  </td>
                  {columns.slice(1).map(c => (
                    <td key={c.key} className="nv-num"
                      style={c.warn ? { color: r[c.key] > 0 ? T.amber : T.textMuted } : undefined}>
                      {c.bar
                        ? <MagnitudeBar value={r[c.key] ?? 0} max={maxima[c.key]} color={c.bar} />
                        : (r[c.key] != null ? r[c.key] : '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>

            {!loading && shownCount > 0 && (
              <tfoot>
                <tr>
                  <td style={{ color: T.textMuted, letterSpacing: '0.1em' }}>
                    TOTAL
                    {q && <span style={{ fontWeight: 400, fontSize: 9.5, color: 'var(--nd-w20)', marginLeft: 7 }}>all rows — search not applied</span>}
                  </td>
                  {isLog ? (
                    <>
                      {/* Date · Of day · In · Out */}
                      <td colSpan={4} />
                      <td className="nv-num">
                        {visibleRecords.reduce((s, r) => s + (Number(r.hours_worked) || 0), 0).toFixed(1)}
                      </td>
                      {/* Client · Site · City · Shift · Status · Photos */}
                      <td colSpan={6} />
                    </>
                  ) : (
                    columns.slice(1).map(c => (
                      <td key={c.key} className="nv-num" style={c.warn ? { color: T.amber } : undefined}>
                        {c.key === 'jobs' ? (kpis.total_jobs ?? 0)
                          : c.key === 'checkins' ? totalCheckins
                          : c.key === 'people' ? (kpis.people ?? 0)
                          : c.key === 'days' ? (kpis.active_days ?? 0)
                          : c.key === 'total_hours' ? (kpis.total_hours != null ? kpis.total_hours : '—')
                          : c.key === 'avg_hours' ? (kpis.avg_hours != null ? kpis.avg_hours : '—')
                          : c.key === 'missing_clockout' ? missingOut
                          : '—'}
                      </td>
                    ))
                  )}
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {!isLog && (
          <div style={{ padding: '10px 18px', borderTop: `1px solid ${T.border}`, fontSize: 10, color: T.textMuted, lineHeight: 1.6 }}>
            Totals are counted over the whole filtered range, not summed from the rows above — a job worked by two people counts once.
            {missingOut > 0 && (
              <> Hours cover only shifts with a clock-out; <strong style={{ color: T.amber }}>{missingOut}</strong> check-in{missingOut === 1 ? ' has' : 's have'} none, so hours read low.</>
            )}
          </div>
        )}
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 24 }}>
          <img src={lightbox} alt="Attendance" style={{ maxWidth: '90vw', maxHeight: '88vh', borderRadius: 12, border: '1px solid var(--nd-w15)' }} />
        </div>
      )}
    </div>
  );
};

export default AttendanceDashboard;
